import { useEffect, useRef } from 'react';
import { cn } from '@immo/ui-kit';
import type { FleetAssistantMessageBody, FleetAssistantMessageView } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { useLocale } from '@/i18n/provider';
import { SparkleIcon } from '@/components/layout/icons';
import { ChatMarkdown } from '@/components/chat/chat-markdown';

type Props = {
  readonly messages: ReadonlyArray<FleetAssistantMessageView>;
  readonly pending: boolean;
  /** Off where the list sits inside a scrolling region of its own: `scrollIntoView` would drag every ancestor too. */
  readonly scrollToLatest: boolean;
};

function isToday(date: Date): boolean {
  const now = new Date();

  return (
    date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate()
  );
}

function AssistantBody({ body }: { readonly body: FleetAssistantMessageBody }) {
  const t = useTranslations('notices.messenger.fleet-assistant');

  switch (body.kind) {
    case 'text':
      return <ChatMarkdown content={body.text} />;
    case 'asks-for-ticket':
      return body.tickets.length === 0 ? (
        <>{t('asks-for-ticket-none')}</>
      ) : (
        <>{t('asks-for-ticket', { tickets: body.tickets.join(', ') })}</>
      );
    case 'refused':
      return <>{t('refused')}</>;
    case 'answer-recorded':
      return <>{t('answer-recorded', { ticket: body.ticket, answer: body.answer })}</>;
    case 'already-answered':
      return <>{t('already-answered', { ticket: body.ticket, answer: body.answer, name: body.answeredBy })}</>;
    case 'failed':
      return <>{t('failed')}</>;
  }
}

function MessageMeta({ message, own }: { readonly message: FleetAssistantMessageView; readonly own: boolean }) {
  const t = useTranslations('pages.admin.fleet-board.assistant');
  const { locale } = useLocale();
  const created = new Date(message.createdAt);
  const time = isToday(created)
    ? created.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
    : created.toLocaleString(locale, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  const parts = [
    message.context.ticket === null ? null : (
      <span key="ticket" className="font-mono">
        {message.context.ticket}
      </span>
    ),
    <span key="time">{time}</span>,
    message.channel === 'telegram' ? <span key="channel">{t('channel-telegram')}</span> : null,
  ].filter((part) => part !== null);

  return (
    <p className={cn('mt-1 flex gap-1 text-xs text-text-tertiary', own ? 'justify-end' : 'justify-start')}>
      {parts.flatMap((part, index) => (index === 0 ? [part] : [<span key={`sep-${part.key}`}>·</span>, part]))}
    </p>
  );
}

export function AssistantMessageList({ messages, pending, scrollToLatest }: Props) {
  const t = useTranslations('pages.admin.fleet-board.assistant');
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollToLatest) bottomRef.current?.scrollIntoView?.({ block: 'end' });
  }, [messages.length, pending, scrollToLatest]);

  return (
    <div className="flex flex-col gap-4 p-4">
      {messages.map((message) => {
        const own = message.author === 'operator';
        const note = message.body.kind === 'failed';

        return (
          <div key={message.id} className={cn('flex flex-col', own ? 'items-end' : 'items-start')}>
            <div
              className={cn(
                'max-w-[80%] rounded-2xl px-4 py-2.5 text-base',
                own
                  ? 'whitespace-pre-wrap bg-interactive text-interactive-on'
                  : note
                    ? 'bg-subtle italic text-text-secondary'
                    : 'bg-subtle text-text-primary',
              )}
            >
              {message.body.kind === 'text' && own ? message.body.text : <AssistantBody body={message.body} />}
            </div>
            <MessageMeta message={message} own={own} />
          </div>
        );
      })}
      {pending && (
        <div role="status" aria-label={t('working')} className="flex items-center gap-2 self-start text-text-tertiary">
          <SparkleIcon aria-hidden className="h-4 w-4 animate-spin [animation-duration:1.6s]" />
          <span aria-hidden>…</span>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}
